import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Target, Calendar as CalendarIcon, TrendingUp, Trash2, AlertCircle, CheckCircle2, Repeat, Wallet, Pencil } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import EditSavingsGoalDialog from './EditSavingsGoalDialog';
import { API_URL } from '../config/api';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

const SavingsGoalCard = ({ goal, progress, currency, onDeleted, onUpdated }) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!progress) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-lg">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/savings-goals/${goal.id}`, { withCredentials: true });
      toast.success('Objectif et versements automatiques supprimés');
      onDeleted();
    } catch (error) {
      console.error('Error deleting savings goal:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const isCompleted = progress.progress_percentage >= 100;
  const contributionDay = goal.contribution_day || 1;

  return (
    <>
      <div className={`bg-white rounded-2xl p-5 sm:p-6 shadow-lg border-2 ${
        isCompleted ? 'border-teal-400 bg-gradient-to-br from-teal-50 to-cyan-50' : 'border-teal-200'
      } hover:shadow-xl transition-all`}>
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
              isCompleted ? 'bg-gradient-to-br from-teal-500 to-cyan-500' : 'bg-gradient-to-br from-teal-400 to-cyan-400'
            }`}>
              {isCompleted ? (
                <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              ) : (
                <Target className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              )}
            </div>
            <div className="min-w-0">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {goal.name}
              </h3>
              {goal.mode === 'automatic' && (
                <div className="flex items-center gap-1 text-xs sm:text-sm text-teal-600">
                  <Repeat className="h-3 w-3" />
                  <span>{goal.contribution_amount || 5}€/mois le {contributionDay}</span>
                </div>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowEditDialog(true)}
            className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDeleteDialog(true)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Progress */}
        <div className="space-y-3 mb-4">
          <div className="flex justify-between items-baseline flex-wrap gap-1">
            <span className="text-2xl sm:text-3xl font-bold text-gray-900">
              {progress.current_amount.toFixed(0)} {currency}
            </span>
            <span className="text-base sm:text-lg text-gray-600">
              / {goal.target_amount.toFixed(0)} {currency}
            </span>
          </div>
          
          <Progress value={progress.progress_percentage} className="h-3" />
          
          <div className="flex justify-between items-center flex-wrap gap-2">
            <span className={`text-sm font-semibold ${
              isCompleted ? 'text-teal-600' : 'text-cyan-600'
            }`}>
              {progress.progress_percentage.toFixed(0)}% atteints
            </span>
            {!isCompleted && (
              <span className="text-sm text-gray-600">
                Reste {progress.remaining_amount.toFixed(0)} {currency}
              </span>
            )}
          </div>
        </div>

        {/* Automatic contributions info */}
        {goal.mode === 'automatic' && !isCompleted && (
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg p-3 mb-4 border border-teal-100">
            <div className="flex items-start gap-2">
              <Wallet className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs sm:text-sm text-teal-700">
                <p className="font-medium">Versement automatique actif</p>
                <p className="text-teal-600">
                  {goal.contribution_amount || 5}€ débités le {contributionDay} de chaque mois
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="space-y-2 text-sm">
          {goal.target_date && (
            <div className="flex items-center gap-2 text-gray-600">
              <CalendarIcon className="h-4 w-4 flex-shrink-0" />
              <span>
                Objectif pour le {formatDate(goal.target_date)}
                {progress.on_track !== null && (
                  <span className={`ml-2 font-semibold ${
                    progress.on_track ? 'text-green-600' : 'text-orange-600'
                  }`}>
                    {progress.on_track ? '✓ Dans les temps' : '⚠ En retard'}
                  </span>
                )}
              </span>
            </div>
          )}
          
          {progress.estimated_months_remaining && !isCompleted && (
            <div className="flex items-center gap-2 text-gray-600">
              <TrendingUp className="h-4 w-4 flex-shrink-0" />
              <span>
                ~{Math.ceil(progress.estimated_months_remaining)} mois restants
              </span>
            </div>
          )}

          {isCompleted && (
            <div className="flex items-center gap-2 text-teal-600 font-semibold bg-teal-100 px-3 py-2 rounded-lg">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              <span>Bien joué ! Objectif atteint 🎉</span>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet objectif ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. L'objectif et ses versements automatiques seront supprimés.
              Les transactions déjà effectuées resteront dans l'historique.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditSavingsGoalDialog
        goal={goal}
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        onSaved={() => {
          setShowEditDialog(false);
          if (onUpdated) onUpdated();
        }}
      />
    </>
  );
};

export default SavingsGoalCard;